import { useEffect, useRef, useState } from 'react'
import Icon from '../components/Icon'
import { tw } from './strings'
import { sv } from './servicesStrings'
import { COUNTS, TAB, emit, GOTO, type CountsDetail, type GotoDetail, type TabDetail } from './desktop'
import type { Side } from './SideSwitch'

/**
 * Jobs, Services, My activity: the same three words for everyone.
 *
 *   Jobs      worker: the job board. employer: their postings. pro: the job board, read-only.
 *   Services  pro: find a pro. worker and employer: the same list, over their screen.
 *   My activity   a menu of that role's own screens (applications, messages, profile...).
 *
 * It changes nothing a screen does; it only chooses which existing screen is showing.
 */
export type Browse = 'pros' | 'jobs' | null

interface Entry { tab: string; label: string; badge?: number }

function activity(side: Side): Entry[] {
  if (side === 'worker') return [
    { tab: 'applications', label: tw('w.tab.applications') },
    { tab: 'saved', label: tw('w.tab.saved') },
    { tab: 'messages', label: tw('w.tab.messages') },
    { tab: 'profile', label: tw('w.tab.profile') },
  ]
  if (side === 'employer') return [
    { tab: 'home', label: tw('w.tab.home') },
    { tab: 'applicants', label: tw('w.tab.applicants') },
    { tab: 'messages', label: tw('w.tab.messages') },
    { tab: 'business', label: tw('w.tab.business') },
  ]
  return [
    { tab: 'requests', label: sv('sv.tab.requests') },
    { tab: 'saved', label: sv('sv.tab.saved') },
    { tab: 'ads', label: sv('sv.tab.ads') },
    { tab: 'profile', label: tw('w.tab.profile') },
  ]
}

export default function DesktopNav({ side, browsing, onBrowse }: {
  side: Side
  browsing: Browse
  onBrowse: (next: Browse) => void
}) {
  const [current, setCurrent] = useState<TabDetail | null>(null)
  const [counts, setCounts] = useState<CountsDetail>({ unread: 0, requests: 0 })
  const [open, setOpen] = useState(false)
  const menu = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onTab = (e: Event) => setCurrent((e as CustomEvent<TabDetail>).detail)
    const onCounts = (e: Event) => setCounts((e as CustomEvent<CountsDetail>).detail)
    window.addEventListener(TAB, onTab)
    window.addEventListener(COUNTS, onCounts)
    return () => { window.removeEventListener(TAB, onTab); window.removeEventListener(COUNTS, onCounts) }
  }, [])

  // The menu closes on any outside tap and on Escape, like every menu.
  useEffect(() => {
    if (!open) return
    const outside = (e: MouseEvent) => { if (menu.current && !menu.current.contains(e.target as Node)) setOpen(false) }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', outside)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', esc) }
  }, [open])

  const here = current?.side === side ? current.tab : null
  const go = (tab: string) => { onBrowse(null); emit<GotoDetail>(GOTO, { tab }); setOpen(false) }

  const jobsTab = side === 'employer' ? 'postings' : 'jobs'
  const servicesTab = 'find'
  const jobsCurrent = side === 'pro' ? browsing === 'jobs' : !browsing && here === jobsTab
  const servicesCurrent = side === 'pro' ? !browsing && here === servicesTab : browsing === 'pros'
  const items = activity(side).map((e) => ({
    ...e, badge: e.tab === 'messages' ? counts.unread : e.tab === 'ads' ? counts.requests : 0,
  }))
  const activityCurrent = !browsing && items.some((e) => e.tab === here)
  const waiting = items.reduce((n, e) => n + (e.badge ?? 0), 0)

  return (
    <nav className="w-dnav" aria-label={tw('w.nav.main')}>
      <button
        aria-current={jobsCurrent ? 'page' : undefined}
        onClick={() => { if (side === 'pro') onBrowse('jobs'); else go(jobsTab) }}
      >{tw('w.nav.jobs')}</button>
      <button
        aria-current={servicesCurrent ? 'page' : undefined}
        onClick={() => { if (side === 'pro') go(servicesTab); else onBrowse('pros') }}
      >{tw('w.nav.services')}</button>

      <div className="w-dnav-menu" ref={menu}>
        <button
          aria-current={activityCurrent ? 'page' : undefined}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {tw('w.nav.activity')}
          {waiting > 0 && <span className="w-dnav-dot" aria-label={`${waiting} ${tw('w.unread')}`} />}
          <Icon name="chevron" size={14} className="w-dnav-caret" />
        </button>
        {open && (
          <div className="w-dnav-pop" role="menu">
            {items.map((e) => (
              <button key={e.tab} role="menuitem" aria-current={!browsing && here === e.tab ? 'page' : undefined} onClick={() => go(e.tab)}>
                {e.label}
                {Boolean(e.badge) && <span className="w-dnav-count">{e.badge}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </nav>
  )
}
